import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import VideoUploader from '@/components/VideoUploader';
import { Button } from '@/components/ui/button';
import DocumentTitle from '@/components/DocumentTitle';

const Upload = () => {
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <DocumentTitle 
        title="Upload Video - VideoBrain.ai | Add Videos to Your Library"
        description="Upload and process your videos with VideoBrain.ai. Our AI will analyze, transcribe, and index your content for powerful search capabilities."
      />
      
      <div className="mb-6">
        <Button variant="ghost" asChild className="pl-0 mb-4">
          <Link to="/" className="flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>
        </Button>
        
        <h1 className="text-3xl font-bold mb-2">Upload Video</h1>
        <p className="text-muted-foreground max-w-2xl">
          Upload your video to start analyzing and extracting content using our powerful AI tools.
        </p>
      </div>
      
      <div className="max-w-3xl mx-auto">
        <VideoUploader />
        
        <div className="mt-10">
          <h2 className="text-xl font-semibold mb-4">What happens next?</h2>
          <ol className="space-y-4">
            <li className="flex gap-3">
              <div className="flex-none flex items-center justify-center w-7 h-7 rounded-full bg-video-blue text-white text-sm font-medium mt-0.5">
                1
              </div>
              <div>
                <strong className="font-medium">Video Processing</strong>
                <p className="text-muted-foreground">
                  We'll process your video to extract transcripts, identify speakers, and detect objects.
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <div className="flex-none flex items-center justify-center w-7 h-7 rounded-full bg-video-blue text-white text-sm font-medium mt-0.5">
                2
              </div>
              <div>
                <strong className="font-medium">Smart Indexing</strong>
                <p className="text-muted-foreground">
                  Your video content will be indexed for advanced search capabilities and quick access.
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <div className="flex-none flex items-center justify-center w-7 h-7 rounded-full bg-video-blue text-white text-sm font-medium mt-0.5">
                3
              </div>
              <div>
                <strong className="font-medium">Ready to Use</strong>
                <p className="text-muted-foreground">
                  Once processing is complete, you can search, create clips, and extract insights from your video.
                </p>
              </div>
            </li>
          </ol>
        </div>
        
        <div className="mt-10 bg-muted/50 p-6 rounded-lg">
          <h2 className="text-xl font-semibold mb-2">Supported Formats</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="font-medium mb-1">Video Formats</h3>
              <p className="text-sm text-muted-foreground">MP4, MOV, AVI, MKV</p>
            </div>
            <div>
              <h3 className="font-medium mb-1">Maximum Size</h3>
              <p className="text-sm text-muted-foreground">5 GB per file</p>
            </div>
            <div>
              <h3 className="font-medium mb-1">Resolution</h3>
              <p className="text-sm text-muted-foreground">Up to 4K (3840×2160)</p>
            </div>
            <div>
              <h3 className="font-medium mb-1">Duration</h3>
              <p className="text-sm text-muted-foreground">Up to 4 hours</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Upload;
